import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import {
  Network,
  Plus,
  Search,
  Settings,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowDownLeft,
  ArrowUpRight,
  FileText,
  AlertTriangle,
  Loader2 } from
'lucide-react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle } from
"@/shared/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Label } from "@/shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";


import { oigAPI } from '@/shared/services/api';

export const EDIPartners = () => {const { t } = useTranslation("oig");
  const [partners, setPartners] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPartnerConfig, setSelectedPartnerConfig] = useState(null);
  const [selectedPartnerMessages, setSelectedPartnerMessages] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [partnersData, messagesData] = await Promise.all([
        oigAPI.getEDIPartners(),
        oigAPI.getEDIMessages()]
        );

        setPartners(partnersData || []);
        setMessages(messagesData || []);
      } catch (err) {
        console.error('Failed to fetch EDI data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statusColors = {
    'Ativo': 'bg-emerald-100 text-emerald-700',
    'Inativo': 'bg-gray-100 text-gray-700',
    'Processado': 'bg-emerald-100 text-emerald-700',
    'Enviado': 'bg-blue-100 text-blue-700',
    'Pendente': 'bg-amber-100 text-amber-700',
    'Erro': 'bg-red-100 text-red-700',
    'active': 'bg-emerald-100 text-emerald-700',
    'pending': 'bg-amber-100 text-amber-700'
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="oig-edi-partners">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <Network className="h-8 w-8 text-violet-600" />{t("ediParceiros", "EDI - Parceiros")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("electronicDataInterchange", "Electronic Data Interchange - Gest\xE3o de Parceiros Comerciais")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("sincronizar", "Sincronizar")}

          </Button>
          <Button className="bg-violet-600 hover:bg-violet-700 gap-2">
            <Plus className="h-4 w-4" />{t("novoParceiro", "Novo Parceiro")}

          </Button>
        </div>
      </div>

      {/* Partners Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {partners.map((partner) =>
        <Card key={partner.id} className="card-elevated hover:shadow-lg transition-all">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{partner.nome}</CardTitle>
                  <p className="text-sm text-muted-foreground font-mono">{partner.cnpj}</p>
                </div>
                <Badge className={statusColors[partner.status]}>
                  {partner.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("tipoEdi", "Tipo EDI:")}</span>
                  <Badge variant="outline">{partner.tipoEDI}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("protocolo", "Protocolo:")}</span>
                  <Badge variant="outline">{partner.protocolo}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("docsRecebidos", "Docs Recebidos:")}</span>
                  <span className="font-medium text-emerald-600">{partner.documentosRecebidos}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("docsEnviados", "Docs Enviados:")}</span>
                  <span className="font-medium text-blue-600">{partner.documentosEnviados}</span>
                </div>
                <div className="pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground">{t("ltimaSincronizao", "\xDAltima sincroniza\xE7\xE3o:")}
                  {partner.ultimaSincronizacao ? new Date(partner.ultimaSincronizacao).toLocaleString('pt-BR') : 'Sem dados'}
                  </p>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => setSelectedPartnerConfig(partner)}>
                    <Settings className="h-3 w-3" />{t("configurar", "Configurar")}

                </Button>
                  <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => setSelectedPartnerMessages(partner)}>
                    <FileText className="h-3 w-3" />{t("mensagens", "Mensagens")}

                </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recent Messages */}
      <Card className="card-elevated">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-muted-foreground" />{t("mensagensRecentes", "Mensagens Recentes")}

            </CardTitle>
            <CardDescription>{t("ltimasTransaesEdi", "\xDAltimas transa\xE7\xF5es EDI com parceiros comerciais")}

            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input placeholder="Buscar mensagem..." className="w-64" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {messages.map((msg) =>
            <div
              key={msg.id}
              className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
              
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${msg.direcao === 'Entrada' ?
                'bg-emerald-100 text-emerald-600' :
                'bg-blue-100 text-blue-600'}`
                }>
                    {msg.direcao === 'Entrada' ?
                  <ArrowDownLeft className="h-4 w-4" /> :
                  <ArrowUpRight className="h-4 w-4" />
                  }
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{msg.parceiro}</p>
                      <Badge variant="outline" className="text-xs">{msg.tipo}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {msg.numeroDocumento} • {msg.itens}{t("itens", "itens \u2022")}{formatCurrency(msg.valorTotal)}
                    </p>
                    {msg.erro &&
                  <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                        <AlertTriangle className="h-3 w-3" />
                        {msg.erro}
                      </p>
                  }
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-xs text-muted-foreground">
                    {new Date(msg.dataRecebimento).toLocaleString('pt-BR')}
                  </p>
                  <Badge className={statusColors[msg.status]}>
                    {msg.status === 'Processado' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                    {msg.status === 'Erro' && <XCircle className="h-3 w-3 mr-1" />}
                    {msg.status === 'Pendente' && <Clock className="h-3 w-3 mr-1" />}
                    {msg.status}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Configuration Sheet */}
      <Sheet open={!!selectedPartnerConfig} onOpenChange={(open) => !open && setSelectedPartnerConfig(null)}>
        <SheetContent className="sm:max-w-[500px] overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle>{t("configuraoEdi", "Configura\xE7\xE3o EDI -")}{selectedPartnerConfig?.nome}</SheetTitle>
            <SheetDescription>{t("configureRegrasDe", "Configure regras de transporte e dados para")}
              {selectedPartnerConfig?.nome}.
            </SheetDescription>
          </SheetHeader>
          <Tabs defaultValue="connection">
            <TabsList className="w-full grid-cols-2 grid mb-4">
              <TabsTrigger value="connection">{t("conexo", "Conex\xE3o")}</TabsTrigger>
              <TabsTrigger value="mapping">{t("mapeamento", "Mapeamento")}</TabsTrigger>
            </TabsList>
            <TabsContent value="connection" className="space-y-4">
              <div className="space-y-2">
                <Label>{t("protocoloEdi", "Protocolo EDI")}</Label>
                <Select defaultValue={selectedPartnerConfig?.protocolo?.split(' ')[0]}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o protocolo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EDIFACT">{t("edifact", "EDIFACT")}</SelectItem>
                    <SelectItem value="X12">{t("ansiX12", "ANSI X12")}</SelectItem>
                    <SelectItem value="AS2">{t("as2Protocol", "AS2 Protocol")}</SelectItem>
                    <SelectItem value="CSV">{t("sftpCsv", "S/FTP (CSV)")}</SelectItem>
                    <SelectItem value="API">{t("apiRest", "API REST")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("servidorHost", "Servidor (Host)")}</Label>
                <Input defaultValue={`sftp.${selectedPartnerConfig?.nome?.toLowerCase().replace(/\s+/g, '') || 'parceiro'}.com`} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("porta", "Porta")}</Label>
                  <Input defaultValue="22" />
                </div>
                <div className="space-y-2">
                  <Label>{t("intervaloCron", "Intervalo (Cron)")}</Label>
                  <Input defaultValue="*/15 * * * *" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("usurio", "Usu\xE1rio")}</Label>
                <Input defaultValue="ezerp_service" />
              </div>
              <div className="space-y-2">
                <Label>{t("senhaChave", "Senha / Chave")}</Label>
                <Input type="password" defaultValue="********" />
              </div>
              <div className="pt-4">
                <Button className="w-full bg-violet-600 hover:bg-violet-700">{t("salvarConexo", "Salvar Conex\xE3o")}</Button>
              </div>
            </TabsContent>
            <TabsContent value="mapping" className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground border border-dashed border-gray-300">{t("oMapeamentoBsico", "O mapeamento b\xE1sico de campos (EDIFACT INVOIC/DESADV) \xE9 abstra\xEDdo pela plataforma EzERP OIG. Adicione regras de override pontuais (JSON/JQ) conforme necess\xE1rio.")}

              </div>
              <div className="space-y-2">
                <Label>{t("regrasOverrideJson", "Regras Override (JSON)")}</Label>
                <textarea
                  className="w-full h-40 p-3 rounded-md border border-input bg-background text-sm font-mono focus:ring-2 focus:ring-violet-600 outline-none resize-none"
                  defaultValue={`{\n  "map": {\n    "sender_id": "$.ISA[06]",\n    "doc_ref": "$.BGM[02]"\n  }\n}`} />
                
              </div>
              <div className="pt-2">
                 <Button variant="outline" className="w-full">{t("validarRegras", "Validar Regras")}</Button>
              </div>
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Messages Sheet */}
      <Sheet open={!!selectedPartnerMessages} onOpenChange={(open) => !open && setSelectedPartnerMessages(null)}>
        <SheetContent className="sm:max-w-[600px] overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle>{t("mensagens", "Mensagens -")}{selectedPartnerMessages?.nome}</SheetTitle>
            <SheetDescription>{t("histricoDeDocumentos", "Hist\xF3rico de documentos e pacotes transacionados com este parceiro.")}

            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4">
            {messages.filter((m) => m.parceiro === selectedPartnerMessages?.nome).length === 0 ?
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                <FileText className="h-12 w-12 mb-4 opacity-20" />
                <p>{t("nenhumaMensagemRegistrada", "Nenhuma mensagem registrada nas \xFAltimas 24h.")}</p>
              </div> :

            messages.filter((m) => m.parceiro === selectedPartnerMessages?.nome).map((msg) =>
            <div key={msg.id} className="p-4 border rounded-lg bg-card shadow-sm space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-full ${msg.direcao === 'Entrada' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                        {msg.direcao === 'Entrada' ? <ArrowDownLeft className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />}
                      </div>
                      <div>
                        <span className="font-semibold block">{msg.tipo}</span>
                        <span className="text-xs font-mono text-muted-foreground">{msg.numeroDocumento}</span>
                      </div>
                    </div>
                    <Badge className={statusColors[msg.status]}>{msg.status}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground flex justify-between bg-muted/40 p-2 rounded-md">
                    <div className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(msg.dataRecebimento).toLocaleString('pt-BR')}</div>
                    <span className="font-medium">{msg.itens}{t("volumes", "volumes")}</span>
                  </div>
                  {msg.erro &&
              <div className="p-2 bg-red-50 text-red-600 rounded-md text-xs border border-red-100 flex items-start gap-1.5 mt-2">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      <span className="font-medium">{msg.erro}</span>
                    </div>
              }
                </div>
            )
            }
          </div>
        </SheetContent>
      </Sheet>
    </div>);

};